import { useState, useRef, useCallback } from "react";
import { searchAll, getSuggestions } from "../api/search";

const DEFAULT_PAGES = {
  plannerPage: 1,
  performerPage: 1,
  crewPage: 1,
  blogPage: 1,
};

export function useSearch() {
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("");
  const [results, setResults] = useState(null);
  const [resultsQuery, setResultsQuery] = useState("");
  const [resultsLocation, setResultsLocation] = useState("");
  const [suggestions, setSuggestions] = useState({ categories: [], people: [] });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pages, setPages] = useState(DEFAULT_PAGES);

  const debounceRef = useRef(null);
  const latestQueryRef = useRef("");

  // Core fetch
  const runSearch = useCallback(async (q, loc, pg) => {
    setLoading(true);
    setError(null);
    setShowSuggestions(false);
    try {
      const data = await searchAll(q || "", loc || "", pg);
      setResults(data);
      setResultsQuery(q || "");
      setResultsLocation(loc || "");
    } catch {
      setError("Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced suggestions fetch
  const fetchSuggestions = useCallback((val) => {
    clearTimeout(debounceRef.current);

    if (val.trim().length < 2) {
      setSuggestions({ categories: [], people: [] });
      setShowSuggestions(false);
      return;
    }

    latestQueryRef.current = val;

    debounceRef.current = setTimeout(async () => {
      try {
        const data = await getSuggestions(val);
        if (latestQueryRef.current !== val) return;
        setSuggestions(data);
        setShowSuggestions(data.categories.length > 0 || data.people.length > 0);
      } catch {
        // Swallow suggestion errors silently — main search still works
      }
    }, 300);
  }, []);

  const handleQueryChange = useCallback(
    (val) => {
      setQuery(val);
      fetchSuggestions(val);
    },
    [fetchSuggestions],
  );

  const handleSearch = useCallback(
    (overrideQuery) => {
      const q = overrideQuery ?? query;
      const resetPages = DEFAULT_PAGES;
      setPages(resetPages);
      runSearch(q, location, resetPages);
    },
    [query, location, runSearch],
  );

  const selectSuggestion = useCallback(
    (text) => {
      setQuery(text);
      latestQueryRef.current = text;
      setShowSuggestions(false);
      setSuggestions({ categories: [], people: [] });
      const resetPages = DEFAULT_PAGES;
      setPages(resetPages);
      runSearch(text, location, resetPages);
    },
    [location, runSearch],
  );

  const handlePageChange = useCallback(
    (section, pageNum) => {
      const newPages = { ...pages, [section]: pageNum };
      setPages(newPages);
      runSearch(query, location, newPages);
    },
    [pages, query, location, runSearch],
  );

  const closeSuggestions = useCallback(() => {
    setShowSuggestions(false);
  }, []);

  return {
    query,
    location,
    results,
    resultsQuery,
    resultsLocation,
    suggestions,
    showSuggestions,
    loading,
    error,
    pages,
    setQuery,
    setLocation,
    runSearch,
    handleQueryChange,
    handleSearch,
    selectSuggestion,
    handlePageChange,
    closeSuggestions,
  };
}
